// Meshes for the built-in course: dev-textured boxes, zone outlines, the pit.

import * as THREE from 'three';
import type { Course, SolidType } from '../maps/procedural/course';
import type { BhopBlock } from '../sim/bhopBlocks';
import { BLOCK_TICKS } from '../sim/constants';
import type { Simulation } from '../sim/simulation';
import type { Zone } from '../sim/zones';
import { devTexture } from './devTextures';
import { sizeToThree, sourceToThree } from './coords';
import { disposeTree, type LevelView } from './levelView';

interface BlockView {
  block: BhopBlock;
  mesh: THREE.Mesh;
  mat: THREE.MeshLambertMaterial;
  baseY: number;
}

const size = new THREE.Vector3();
const centre = new THREE.Vector3();

export class CourseView implements LevelView {
  readonly root = new THREE.Group();
  readonly fog: [number, number] = [1800, 7000];
  readonly far = 20000;
  private readonly blockViews: BlockView[] = [];

  constructor(course: Course, anisotropy: number) {
    const tex: Record<SolidType | 'platform', THREE.Texture> = {
      block: devTexture('#c76a2c', 'rgba(60,25,5,.35)', 'rgba(60,25,5,.7)', '64', anisotropy),
      platform: devTexture('#6f757c', 'rgba(20,24,28,.3)', 'rgba(20,24,28,.6)', '128', anisotropy),
      start: devTexture('#5d7563', 'rgba(15,30,18,.3)', 'rgba(15,30,18,.6)', 'start', anisotropy),
      end: devTexture('#7a5a5a', 'rgba(35,12,12,.3)', 'rgba(35,12,12,.6)', 'end', anisotropy),
      checkpoint: devTexture('#5f6f82', 'rgba(14,20,30,.3)', 'rgba(14,20,30,.6)', 'cp', anisotropy),
    };

    // Static solids share one material per type; each bhop block gets its own so it can glow.
    const shared = new Map<SolidType, THREE.MeshLambertMaterial>();
    for (const s of course.solids) {
      const b = s.box;
      sizeToThree(size, b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]);
      const geo = new THREE.BoxGeometry(size.x, size.y, size.z);
      // One texture tile per 128 units on every face.
      const uv = geo.attributes.uv as THREE.BufferAttribute;
      const dims = [
        [size.z, size.y],
        [size.z, size.y],
        [size.x, size.z],
        [size.x, size.z],
        [size.x, size.y],
        [size.x, size.y],
      ];
      for (let f = 0; f < 6; f++)
        for (let v = 0; v < 4; v++) {
          const i = f * 4 + v;
          uv.setXY(i, (uv.getX(i) * dims[f][0]) / 128, (uv.getY(i) * dims[f][1]) / 128);
        }
      let mat: THREE.MeshLambertMaterial;
      if (s.block) mat = new THREE.MeshLambertMaterial({ map: tex.block });
      else {
        mat = shared.get(s.type) ?? new THREE.MeshLambertMaterial({ map: tex[s.type] ?? tex.platform });
        shared.set(s.type, mat);
      }
      const mesh = new THREE.Mesh(geo, mat);
      sourceToThree(
        mesh.position,
        (b.min[0] + b.max[0]) / 2,
        (b.min[1] + b.max[1]) / 2,
        (b.min[2] + b.max[2]) / 2,
      );
      if (!s.block) mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      this.root.add(mesh);
      if (s.block) this.blockViews.push({ block: s.block, mesh, mat, baseY: mesh.position.y });
    }

    this.zone(course.startZone, 0x4fd27a);
    this.zone(course.endZone, 0xff5a5a);

    const pitTex = devTexture('#150c0e', 'rgba(150,40,40,.25)', 'rgba(220,70,60,.55)', '', anisotropy);
    pitTex.repeat.set(60, 60);
    const pit = new THREE.Mesh(
      new THREE.PlaneGeometry(30000, 30000),
      new THREE.MeshBasicMaterial({ map: pitTex }),
    );
    pit.rotation.x = -Math.PI / 2;
    sourceToThree(pit.position, 2500, -2000, -520);
    this.root.add(pit);
  }

  private zone(z: Zone, color: number) {
    sizeToThree(size, z.max[0] - z.min[0], z.max[1] - z.min[1], z.max[2] - z.min[2]);
    const geo = new THREE.BoxGeometry(size.x - 4, size.y, size.z - 4);
    const fill = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.07,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geo),
      new THREE.LineBasicMaterial({ color }),
    );
    sourceToThree(centre, (z.min[0] + z.max[0]) / 2, (z.min[1] + z.max[1]) / 2, 0);
    centre.y = Math.max(z.min[2], 0) + size.y / 2;
    fill.position.copy(centre);
    edges.position.copy(centre);
    this.root.add(fill, edges);
  }

  /** Move dropped blocks and make the one under the player glow red as its contact time runs out. */
  update(sim: Simulation) {
    const contactBlock = sim.contact.block;
    const contactTicks = sim.contact.ticks;
    const blockTicks = BLOCK_TICKS;
    for (const v of this.blockViews) {
      const b = v.block;
      v.mesh.position.y = v.baseY + b.offset;
      v.mesh.visible = b.state !== 'down';
      if (b.state === 'falling' || b.state === 'down') v.mat.emissive.setRGB(0.4, 0.06, 0.02);
      else if (b === contactBlock) {
        const k = Math.min(1, contactTicks / blockTicks);
        v.mat.emissive.setRGB(0.4 * k, 0.07 * k, 0.02 * k);
      } else v.mat.emissive.setRGB(0, 0, 0);
    }
  }

  dispose() {
    disposeTree(this.root);
  }
}
